/**
 * Variables `{{N}}` de una plantilla: detectar, insertar, sincronizar y renumerar. Puras.
 *
 * Twilio usa UN solo espacio de variables por Content: las del cuerpo, el pie, las URLs de los
 * botones y la imagen se numeran juntas (SPEC-backend §6.5, `variablesDePlantilla`). Si el front
 * contara sólo las del cuerpo, un botón `https://tu.web/promo/{{1}}` quedaría sin ejemplo y el
 * validador del backend lo rechazaría sin que la pantalla supiera por qué.
 *
 * Sólo se reconoce `{{N}}` bien formado. Lo mal formado (`{{ 1 }}`, `{{nombre}}`) lo reporta el
 * validador del backend (`formato_variable`); el front no lo duplica.
 */

import type { PlantillaBorrador, VariablePlantilla } from "../types";

type ConTextos = Pick<PlantillaBorrador, "cuerpo" | "pie" | "botones" | "mediaUrl"> & { tipo?: PlantillaBorrador["tipo"] };

const RE_VAR = /\{\{(\d+)\}\}/g;

/**
 * Los textos donde puede haber variables, en el orden en que se numeran: cuerpo, pie, URLs de
 * botones e imagen. Mismo set que `variablesDePlantilla` del backend, que parte del cuerpo
 * COMPUESTO: con `whatsapp/card` el pie es el `footer` y no entra al cuerpo, así que tampoco entra
 * acá (D-A). El encabezado de texto de la tarjeta no lleva variables.
 */
function textosConVariables(b: ConTextos): string[] {
  return [
    b.cuerpo || "",
    b.tipo === "whatsapp/card" ? "" : b.pie || "",
    ...(b.botones || []).map((x) => x.url || ""),
    b.mediaUrl || "",
  ];
}

/** Números de {{N}} bien formados, en orden de aparición y sin repetir, en cuerpo + pie + URLs de botones + mediaUrl. */
export function detectarNumeros(b: ConTextos): number[] {
  const vistos: number[] = [];
  for (const t of textosConVariables(b)) {
    for (const m of t.matchAll(RE_VAR)) {
      const n = Number(m[1]);
      if (!vistos.includes(n)) vistos.push(n);
    }
  }
  return vistos;
}

/** El siguiente número libre: max(detectados) + 1 (o 1). */
export function siguienteNumero(b: ConTextos): number {
  const nums = detectarNumeros(b);
  return nums.length ? Math.max(...nums) + 1 : 1;
}

/**
 * Inserta `{{n}}` en [inicio, fin) del texto (reemplaza la selección) y devuelve la posición del
 * cursor justo después. Portado de `TemplateBuilder.tsx` como función pura, con los extremos
 * acotados: un `selectionStart` fuera de rango no puede duplicar ni perder texto.
 */
export function insertarVariable(texto: string, inicio: number, fin: number, n: number): { texto: string; cursor: number } {
  const t = texto || "";
  const acotar = (x: number) => Math.max(0, Math.min(t.length, Number.isFinite(x) ? Math.trunc(x) : t.length));
  let a = acotar(inicio);
  let z = acotar(fin);
  if (a > z) [a, z] = [z, a];
  const token = `{{${n}}}`;
  return { texto: t.slice(0, a) + token + t.slice(z), cursor: a + token.length };
}

/**
 * Deja `variables` alineado con lo que hay en los textos: conserva alias/ejemplo/campo de los
 * números que siguen, crea `{numero, alias:"Variable N", ejemplo:""}` para los nuevos y saca los
 * que ya no están. Ordenado por número.
 */
export function sincronizarVariables(b: PlantillaBorrador): VariablePlantilla[] {
  const previas = new Map((b.variables || []).map((v) => [v.numero, v]));
  return detectarNumeros(b)
    .slice()
    .sort((x, y) => x - y)
    .map((n) => previas.get(n) ?? { numero: n, alias: `Variable ${n}`, ejemplo: "" });
}

/**
 * Renumera 1..N por orden de primera aparición (cuerpo, pie, botones, imagen) y
 * reescribe los textos y las variables. Idempotente: si ya está en orden, devuelve lo mismo.
 * El reemplazo es de una sola pasada (callback): renumerar {{3}}→{{2}} y {{2}}→{{1}} en pasadas
 * separadas pisaría uno con otro.
 */
export function renumerar(b: PlantillaBorrador): PlantillaBorrador {
  const orden = detectarNumeros(b);
  const mapa = new Map(orden.map((viejo, i) => [viejo, i + 1]));
  const yaOrdenado = orden.every((viejo, i) => viejo === i + 1);
  if (yaOrdenado) return b;

  const rees = (t: string | undefined) =>
    t === undefined ? undefined : t.replace(RE_VAR, (m, d) => (mapa.has(Number(d)) ? `{{${mapa.get(Number(d))}}}` : m));

  const out: PlantillaBorrador = {
    ...b,
    cuerpo: rees(b.cuerpo) ?? "",
    botones: (b.botones || []).map((x) => (x.url === undefined ? x : { ...x, url: rees(x.url) })),
  };
  if (b.pie !== undefined) out.pie = rees(b.pie);
  if (b.mediaUrl !== undefined) out.mediaUrl = rees(b.mediaUrl);
  out.variables = (b.variables || [])
    .filter((v) => mapa.has(v.numero))
    .map((v) => ({ ...v, numero: mapa.get(v.numero)! }));
  out.variables = sincronizarVariables(out);
  return out;
}

/** true si hay saltos (p. ej. {{1}} y {{3}}, o un {{2}} sin {{1}}): habilita el botón «Renumerar». */
export function tieneSaltos(nums: number[]): boolean {
  const u = Array.from(new Set(nums)).sort((x, y) => x - y);
  return u.some((n, i) => n !== i + 1);
}

/** Reemplaza cada {{n}} por su valor (sin valor, deja el token): lo usa la previa local. */
export function reemplazarVariables(texto: string, valores: (n: number) => string | undefined): string {
  return (texto || "").replace(RE_VAR, (m, d) => {
    const v = valores(Number(d));
    return v === undefined ? m : v;
  });
}
